import React, { useState, useEffect } from 'react';
import { ModernNavigation } from '../components/modern/ModernNavigation';
import { ModernFooter } from '../components/modern/ModernFooter';
import '../css/modern-design-system.css';
import '../css/modern-about.css';

export function ModernAboutUs() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="modern-about-page">
      <ModernNavigation scrolled={scrolled} />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg"></div>
        <div className="container">
          <div className="about-hero-content">
            <div className="about-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              <span>Our Story</span>
            </div>
            <h1 className="about-hero-title">Empowering Homeowners to Build Wealth</h1>
            <p className="about-hero-subtitle">
              EquityWorkx was founded with a simple mission: to help homeowners transform their mortgage
              into a powerful wealth-building tool through intelligent insights and personalized strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-mission">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-content">
              <span className="section-label">OUR MISSION</span>
              <h2 className="section-title">Making Financial Freedom Accessible</h2>
              <p>
                We believe that every homeowner deserves access to the financial insights and tools
                that were once only available to the wealthy. Our platform democratizes wealth-building
                by providing personalized strategies based on your unique financial situation.
              </p>
              <p>
                Through cutting-edge technology and a deep understanding of mortgage optimization,
                we help our users make smarter decisions about their home equity, leading to
                faster wealth accumulation and financial independence.
              </p>
            </div>
            <div className="mission-stats">
              <div className="stat-card">
                <div className="stat-number">50K+</div>
                <div className="stat-label">Homeowners Served</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">$2.4M</div>
                <div className="stat-label">Avg. Wealth Built</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">98%</div>
                <div className="stat-label">Customer Satisfaction</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">15 Yrs</div>
                <div className="stat-label">Faster to $1M</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <div className="section-header-modern">
            <span className="section-label">OUR VALUES</span>
            <h2 className="section-title-modern">
              What Drives <span className="text-gradient">Our Team</span>
            </h2>
          </div>

          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h3>Trust & Security</h3>
              <p>
                Your financial data is protected with bank-level encryption. We never sell your
                personal information and maintain the highest security standards.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              </div>
              <h3>Transparency</h3>
              <p>
                We provide clear, honest insights without hidden fees or agendas. Our
                recommendations are based solely on what's best for your financial future.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3>Customer First</h3>
              <p>
                Every feature we build and every decision we make is guided by one question:
                "Will this help our customers build more wealth?"
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <h3>Excellence</h3>
              <p>
                We continuously improve our platform and insights, leveraging the latest
                technology and financial research to deliver exceptional results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="about-how-we-help">
        <div className="container">
          <div className="section-header-modern">
            <span className="section-label">HOW WE HELP</span>
            <h2 className="section-title-modern">
              Your Partner in <span className="text-gradient">Wealth Building</span>
            </h2>
          </div>

          <div className="help-content">
            <div className="help-item">
              <div className="help-number">01</div>
              <div className="help-details">
                <h3>Smart Mortgage Tracking</h3>
                <p>
                  Connect your accounts securely and get real-time insights into your mortgage
                  performance, equity growth, and optimization opportunities.
                </p>
              </div>
            </div>

            <div className="help-item">
              <div className="help-number">02</div>
              <div className="help-details">
                <h3>Personalized Strategies</h3>
                <p>
                  Our AI-powered platform analyzes your unique financial situation to provide
                  customized wealth-building recommendations tailored just for you.
                </p>
              </div>
            </div>

            <div className="help-item">
              <div className="help-number">03</div>
              <div className="help-details">
                <h3>Expert Connections</h3>
                <p>
                  When you're ready, we connect you with qualified financial advisors and
                  mortgage professionals who can help you take action on your opportunities.
                </p>
              </div>
            </div>

            <div className="help-item">
              <div className="help-number">04</div>
              <div className="help-details">
                <h3>Ongoing Support</h3>
                <p>
                  We're with you every step of the way, providing continuous insights,
                  alerts, and guidance as you progress toward your financial goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content-modern">
            <h2 className="cta-title-modern">
              Ready to Start Your <span className="text-gradient">Wealth Journey?</span>
            </h2>
            <p className="cta-subtitle-modern">
              Join thousands of homeowners who are building generational wealth with EquityWorkx.
            </p>
            <div className="cta-buttons-modern">
              <a href="/download" className="btn btn-primary btn-lg">
                Get Started Free
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 10H13M13 10L10 7M13 10L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="/contact" className="btn btn-secondary btn-lg">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
}
